import React from 'react';
import useGlobals from '../../../../../../Hooks/useGlobals';
import Select from '../../../../../Utility/FormElements/Select';
import Input from '../../../../../Utility/FormElements/Input';

interface AuthenticationSectionProps {
    authType: 'none' | 'apiKey' | 'bearer' | 'basic';
    authData: Record<string, any>;
    onAuthTypeChange: (authType: 'none' | 'apiKey' | 'bearer' | 'basic') => void;
    onAuthDataChange: (authUpdates: Record<string, any>) => void;
}

const AuthenticationSection: React.FC<AuthenticationSectionProps> = ({
    authType,
    authData,
    onAuthTypeChange,
    onAuthDataChange,
}) => {
    const { i18n } = useGlobals();

    const renderAuthFields = () => {
        switch (authType) {
            case 'apiKey':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="flex flex-col gap-2">
                                {i18n.translate({ id: 'auth.apiKey.name', sourceName: 'OpenAPI' })}{' '}
                                <Input
                                    inputType="text"
                                    value={authData.apiKeyName || ''}
                                    onChange={(value) => onAuthDataChange({ apiKeyName: value })}
                                    placeholder={i18n.translate({
                                        id: 'auth.apiKey.name.placeholder',
                                        sourceName: 'OpenAPI',
                                    })}
                                    className="w-full"
                                />
                            </label>
                        </div>
                        <div>
                            <label className="flex flex-col gap-2">
                                {i18n.translate({ id: 'auth.apiKey.value', sourceName: 'OpenAPI' })}{' '}
                                <Input
                                    inputType="password"
                                    value={authData.apiKeyValue || ''}
                                    onChange={(value) => onAuthDataChange({ apiKeyValue: value })}
                                    placeholder={i18n.translate({
                                        id: 'auth.apiKey.value.placeholder',
                                        sourceName: 'OpenAPI',
                                    })}
                                    hasVisibilityButton={true}
                                    className="w-full"
                                />
                            </label>
                        </div>
                    </div>
                );
            case 'bearer':
                return (
                    <div>
                        <label className="flex flex-col gap-2">
                            {i18n.translate({ id: 'auth.bearer.token', sourceName: 'OpenAPI' })}
                            <Input
                                inputType="password"
                                value={authData.token || ''}
                                onChange={(value) => onAuthDataChange({ token: value })}
                                placeholder={i18n.translate({
                                    id: 'auth.bearer.token.placeholder',
                                    sourceName: 'OpenAPI',
                                })}
                                hasVisibilityButton={true}
                                className="w-full"
                            />
                        </label>
                    </div>
                );
            case 'basic':
                return (
                    <div className="space-y-4">
                        <div>
                            <label className="flex flex-col gap-2">
                                {i18n.translate({ id: 'auth.basic.username', sourceName: 'OpenAPI' })}{' '}
                                <Input
                                    inputType="text"
                                    value={authData.username || ''}
                                    onChange={(value) => onAuthDataChange({ username: value })}
                                    placeholder={i18n.translate({
                                        id: 'auth.basic.username.placeholder',
                                        sourceName: 'OpenAPI',
                                    })}
                                    className="w-full"
                                />
                            </label>
                        </div>
                        <div>
                            <label className="flex flex-col gap-2">
                                {i18n.translate({ id: 'auth.basic.password', sourceName: 'OpenAPI' })}{' '}
                                <Input
                                    inputType="password"
                                    value={authData.password || ''}
                                    onChange={(value) => onAuthDataChange({ password: value })}
                                    placeholder={i18n.translate({
                                        id: 'auth.basic.password.placeholder',
                                        sourceName: 'OpenAPI',
                                    })}
                                    hasVisibilityButton={true}
                                    className="w-full"
                                />
                            </label>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div>
            <div className="my-4">
                <h4>{i18n.translate({ id: 'form.authentication.title', sourceName: 'OpenAPI' })}</h4>
            </div>

            <div>
                <label className="flex flex-col gap-2 text-sm">
                    {i18n.translate({ id: 'form.authType.label', sourceName: 'OpenAPI' })}{' '}
                    <Select
                        value={authType}
                        onChange={(value) => onAuthTypeChange(value as 'none' | 'apiKey' | 'bearer' | 'basic')}
                    >
                        <option value="none">{i18n.translate({ id: 'auth.none', sourceName: 'OpenAPI' })}</option>{' '}
                        <option value="apiKey">{i18n.translate({ id: 'auth.apiKey', sourceName: 'OpenAPI' })}</option>
                        <option value="bearer">{i18n.translate({ id: 'auth.bearer', sourceName: 'OpenAPI' })}</option>
                        <option value="basic">{i18n.translate({ id: 'auth.basic', sourceName: 'OpenAPI' })}</option>
                    </Select>
                </label>
            </div>

            {authType !== 'none' && (
                <div className="mt-6 py-4 bg-layout-backgroundColor/30 rounded-lg">
                    <h5 className="mb-4">{i18n.translate({ id: 'form.authFields.title', sourceName: 'OpenAPI' })}</h5>
                    {renderAuthFields()}
                </div>
            )}
        </div>
    );
};

export default AuthenticationSection;
